/**
 * Error monitoring scaffold.
 *
 * Sentry is optional — it only activates when:
 *   1. REACT_APP_SENTRY_DSN is set at build time, AND
 *   2. The user has accepted cookies (see CookieConsent), AND
 *   3. @sentry/react is installed as a dependency.
 *
 * The dynamic import keeps Sentry out of the main bundle for users who
 * haven't consented or don't have a DSN configured. If the package isn't
 * installed, the catch below silently no-ops so the app still boots.
 */

import { hasAnalyticsConsent } from '../components/CookieConsent';

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENVIRONMENT =
  process.env.REACT_APP_ENVIRONMENT ||
  (process.env.NODE_ENV === 'production' ? 'production' : 'development');
const RELEASE = process.env.REACT_APP_VERSION || undefined;

let _initialized = false;
let _Sentry = null;

export async function initMonitoring() {
  if (_initialized) return _Sentry;
  if (!DSN) return null;
  if (!hasAnalyticsConsent()) return null;

  try {
    // Optional lazy dependency; deployments without Sentry should still build.
    // eslint-disable-next-line
    const Sentry = await import(/* webpackChunkName: "sentry" */ '@sentry/react');
    Sentry.init({
      dsn: DSN,
      environment: ENVIRONMENT,
      release: RELEASE,
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 0.0,
      // Only capture in production; dev noise clutters the dashboard.
      enabled: ENVIRONMENT === 'production',
      beforeSend(event) {
        // Strip anything that looks like an auth header / token.
        try {
          if (event.request?.headers) {
            for (const k of Object.keys(event.request.headers)) {
              if (/auth|token|cookie|key/i.test(k)) {
                event.request.headers[k] = '[redacted]';
              }
            }
          }
        } catch {
          /* ignore */
        }
        return event;
      },
    });
    _Sentry = Sentry;
    _initialized = true;
    return Sentry;
  } catch (err) {
    // Package not installed or failed to load — fall through to no-op.
    if (ENVIRONMENT !== 'production') {
      // eslint-disable-next-line no-console
      console.info('Sentry skipped:', err?.message || err);
    }
    return null;
  }
}

/** Report a handled error from anywhere in the app (no-op if not initialized). */
export function captureException(err, context) {
  if (_Sentry) {
    try {
      _Sentry.captureException(err, { extra: context });
    } catch {
      /* ignore */
    }
  } else if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('[monitoring]', err, context);
  }
}

/** Tag the current user so Sentry can link errors to sessions. */
export function setMonitoringUser(user) {
  if (!_Sentry) return;
  try {
    _Sentry.setUser(user ? { id: user.user_id || user.id, email: undefined } : null);
  } catch {
    /* ignore */
  }
}

// Re-init if the user accepts cookies after the page loads.
if (typeof window !== 'undefined') {
  window.addEventListener('roomhora:consent', (e) => {
    if (e?.detail?.choice === 'accepted' && !_initialized) {
      initMonitoring();
    }
  });
}
